import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BrandLogo } from "@/components/BrandLogo";
import { toast } from "sonner";
import { Mail, ArrowLeft, CheckCircle2, ExternalLink } from "lucide-react";

/**
 * ForgotPassword — collects an email, calls POST /api/v1/auth/forgot-password.
 * In dev/preview, the backend returns `dev_reset_token` which we render inline
 * as a direct "Reset now" link so the flow is complete without an email provider.
 * In production, the response won't include the token — the user checks their email.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null); // { message, dev_reset_token? }
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(data);
    } catch (err) { toast.error(errMsg(err)); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid place-items-center bg-secondary/40 px-4 py-10" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-4" data-testid="fp-back">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
        <div className="bg-card rounded-2xl border shadow-sm p-6">
          <div className="flex justify-center mb-4"><BrandLogo size={28} /></div>

          {!sent ? (
            <form onSubmit={submit} className="space-y-4" data-testid="fp-form">
              <div className="text-center">
                <h1 className="text-xl md:text-2xl font-display font-semibold">Forgot your password?</h1>
                <p className="text-sm text-muted-foreground mt-1">Enter your registered email — we&apos;ll send you a link to reset it.</p>
              </div>
              <div>
                <Label>Email</Label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="pl-9 h-10" data-testid="fp-email" />
                </div>
              </div>
              <Button type="submit" disabled={loading || !email} className="w-full btn-primary-glow h-11" data-testid="fp-submit">
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          ) : (
            <div className="text-center space-y-3" data-testid="fp-sent">
              <div className="w-12 h-12 mx-auto rounded-full bg-primary/10 text-primary grid place-items-center">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <h2 className="text-xl font-display font-semibold">Check your email</h2>
              <p className="text-sm text-muted-foreground">{sent.message}</p>

              {sent.dev_reset_token && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-left space-y-2" data-testid="fp-dev-token">
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-amber-800">Preview environment</div>
                  <p className="text-xs text-amber-900">Email delivery isn&apos;t configured yet. Click below to reset now:</p>
                  <Button asChild size="sm" variant="outline" className="w-full border-amber-400 hover:bg-amber-100" data-testid="fp-dev-open">
                    <Link to={`/reset-password?token=${encodeURIComponent(sent.dev_reset_token)}`}>
                      Open reset link <ExternalLink className="w-3.5 h-3.5 ml-1" />
                    </Link>
                  </Button>
                </div>
              )}

              <Button variant="ghost" size="sm" onClick={() => nav("/login")} className="w-full mt-2" data-testid="fp-back-to-login">
                Back to sign in
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
